import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Modal } from "react-bootstrap";
import { Button } from "../../Controls/Button";
import { HeaderText } from "../../Controls/HeaderText";
import jsQR from "jsqr";
import { CheckCircle, Clock, ExclamationCircle, QrCode, XCircle } from "react-bootstrap-icons";

const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: "environment",
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
};

export interface QrSessionInfo {
  status: "active" | "expired" | "upcoming" | "not_found";
  from?: string;
  to?: string;
  plate?: string;
  /** Present when a matching session was found; used for server entry-confirm (not shown in UI). */
  reservationId?: number;
}

export interface QrScannerModalProps {
  show: boolean;
  onHide: () => void;
  getConfirmationSession: (confirmationNum: string) => QrSessionInfo | null;
  /**
   * Called once per successful decode when a result is shown. Monitor uses this for
   * fire-and-forget entry-confirm; do not use response for UI.
   */
  onQrResultShown?: (info: QrSessionInfo) => void;
}

type Stage = "camera" | "result" | "error";

const scannerBrand = "#e91e8c";

const sessionResultTheme: Record<QrSessionInfo["status"], { bg: string; color: string; border: string; icon: React.ReactNode; label: string }> = {
  active:    { bg: "#e9f6df", color: "#2f7d32", border: "#d4ebc4", icon: <CheckCircle size={22} />,       label: "Active session" },
  expired:   { bg: "#f8e8ea", color: "#9f2d2d", border: "#eed2d6", icon: <XCircle size={22} />,           label: "Session expired" },
  upcoming:  { bg: "#dbeafe", color: "#1f5fae", border: "#c7dcf7", icon: <Clock size={22} />,             label: "Upcoming session" },
  not_found: { bg: "#f8efd9", color: "#7a5a18", border: "#eadfbd", icon: <ExclamationCircle size={22} />, label: "No session on file" },
};

export function QrScannerModal({ show, onHide, getConfirmationSession, onQrResultShown }: QrScannerModalProps) {
  const videoRef                  = useRef<HTMLVideoElement>(null);
  const canvasRef                 = useRef<HTMLCanvasElement>(null);
  const streamRef                 = useRef<MediaStream | null>(null);
  const rafRef                    = useRef<number | null>(null);
  const scanningRef               = useRef(false);
  const getConfirmationSessionRef = useRef(getConfirmationSession);
  const onQrResultShownRef        = useRef(onQrResultShown);
  const entryConfirmFiredRef      = useRef(false);

  const [stage, setStage]               = useState<Stage>("camera");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [scannedCode, setScannedCode]   = useState<string | null>(null);
  const [sessionInfo, setSessionInfo]   = useState<QrSessionInfo | null>(null);

  // Keep ref current without triggering effect re-runs
  useEffect(() => { getConfirmationSessionRef.current = getConfirmationSession; });
  useEffect(() => { onQrResultShownRef.current = onQrResultShown; });

  const stopCamera = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    scanningRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startScanLoop = useCallback(() => {
    scanningRef.current = true;

    const tick = () => {
      if (!scanningRef.current) return;
      const video  = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas || !video.videoWidth) {
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      canvas.width  = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) { rafRef.current = requestAnimationFrame(tick); return; }

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(imageData.data, imageData.width, imageData.height, { inversionAttempts: "dontInvert" });

      if (code?.data) {
        scanningRef.current = false;
        const text = code.data.trim();
        const info = getConfirmationSessionRef.current(text);
        setScannedCode(text);
        setSessionInfo(info);
        setStage("result");
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, []); // stable — reads latest getConfirmationSession via ref

  const startCamera = useCallback(async () => {
    stopCamera();
    entryConfirmFiredRef.current = false;
    setErrorMessage(null);
    setScannedCode(null);
    setSessionInfo(null);
    setStage("camera");
    try {
      const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => startScanLoop();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Camera access was denied.";
      setErrorMessage(msg);
      setStage("error");
    }
  }, [stopCamera, startScanLoop]); // stable deps — won't re-run on parent re-renders

  useEffect(() => {
    if (show) {
      entryConfirmFiredRef.current = false;
      void startCamera();
    }
    return () => stopCamera();
  }, [show]); // intentionally only re-run when show changes

  // One notification per scan result (active/upcoming: parent may POST entry-confirm fire-and-forget).
  useEffect(() => {
    if (stage !== "result" || !sessionInfo) return;
    if (entryConfirmFiredRef.current) return;
    entryConfirmFiredRef.current = true;
    onQrResultShownRef.current?.(sessionInfo);
  }, [stage, sessionInfo]);

  const handleClose = useCallback(() => {
    stopCamera();
    setStage("camera");
    setScannedCode(null);
    setSessionInfo(null);
    setErrorMessage(null);
    onHide();
  }, [onHide, stopCamera]);

  const handleRetry = useCallback(() => {
    startCamera();
  }, [startCamera]);

  if (!show) return null;

  const cornerBase: React.CSSProperties = {
    position: "absolute",
    width: 22,
    height: 22,
    borderColor: scannerBrand,
    borderStyle: "solid",
    borderWidth: 0,
    pointerEvents: "none",
    zIndex: 2,
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <HeaderText>{stage === "result" ? "Scan result" : "Scan QR"}</HeaderText>
      </Modal.Header>
      <Modal.Body className="p-3">
        <style>{`
          @keyframes resultFadeIn {
            from { opacity: 0; transform: translateY(6px); }
            to   { opacity: 1; transform: translateY(0); }
          }
          .result-fade-in {
            animation: resultFadeIn 0.35s ease-out forwards;
          }
          @keyframes qrPulse {
            0%, 100% { opacity: 1; }
            50%       { opacity: 0.5; }
          }
          .qr-scanning-icon { animation: qrPulse 1.4s ease-in-out infinite; }
        `}</style>

        {/* Canvas always mounted so canvasRef is valid during scan loop */}
        <canvas ref={canvasRef} style={{ display: "none" }} />

        {/* ── Camera stage ── */}
        {stage === "camera" && (
          <>
            <div className="position-relative rounded-3 overflow-hidden bg-dark mb-3">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-100"
                style={{ display: "block", maxHeight: "60vh", objectFit: "cover" }}
              />
              {/* Corner brackets */}
              <div style={{ ...cornerBase, top: 10, left: 10, borderTopWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, top: 10, right: 10, borderTopWidth: 3, borderRightWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, left: 10, borderBottomWidth: 3, borderLeftWidth: 3 }} />
              <div style={{ ...cornerBase, bottom: 10, right: 10, borderBottomWidth: 3, borderRightWidth: 3 }} />
              {/* Scanning indicator overlay */}
              <div
                className="position-absolute bottom-0 start-0 end-0 d-flex align-items-center justify-content-center pb-3"
                style={{ zIndex: 3 }}
              >
                <span
                  className="d-inline-flex align-items-center gap-2 px-3 py-1 rounded-pill fw-semibold small"
                  style={{ background: "rgba(0,0,0,0.55)", color: "#fff" }}
                >
                  <QrCode size={14} className="qr-scanning-icon" />
                  Scanning…
                </span>
              </div>
            </div>

            <p className="text-muted small mb-0 text-center">
              Point the camera at a QR code — it will be detected automatically
            </p>
          </>
        )}

        {/* ── Result stage ── */}
        {stage === "result" && (() => {
          const status = sessionInfo?.status ?? "not_found";
          const theme = sessionResultTheme[status];
          const sessionWindow = sessionInfo?.from && sessionInfo?.to ? `${sessionInfo.from} - ${sessionInfo.to}` : null;
          const statusMessage = status === "active"
            ? sessionInfo?.to ? `Covered now - ends ${sessionInfo.to}` : "Covered now"
            : status === "upcoming"
              ? sessionWindow ? `Starts later - ${sessionWindow}` : "Starts later"
              : status === "expired"
                ? sessionInfo?.to ? `Expired - ended ${sessionInfo.to}` : "Expired"
                : "QR code not registered for this lot";

          return (
            <div className="result-fade-in">
              <div
                className="d-flex align-items-center gap-3 px-3 py-4 mb-3"
                style={{ background: theme.bg, border: `1px solid ${theme.border}`, color: theme.color }}
              >
                <div className="d-flex align-items-center justify-content-center flex-shrink-0">
                  {theme.icon}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="fw-bold" style={{ fontSize: "1.15rem" }}>
                    {theme.label}
                  </div>
                  <div className="fw-semibold small">{statusMessage}</div>
                </div>
              </div>

              <div className="row g-3 px-3 pb-3">
                {sessionInfo?.plate && (
                  <div className="col-6 col-md-4">
                    <div className="text-muted fw-semibold" style={{ fontSize: "0.65rem" }}>Plate</div>
                    <div className="fw-semibold" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{sessionInfo.plate}</div>
                  </div>
                )}
                <div className="col-6 col-md-4">
                  <div className="text-muted fw-semibold" style={{ fontSize: "0.65rem" }}>Confirmation</div>
                  <div className="fw-semibold" style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{scannedCode ?? "-"}</div>
                </div>
                {sessionWindow && (
                  <div className="col-12 col-md-4">
                    <div className="text-muted fw-semibold" style={{ fontSize: "0.65rem" }}>Window</div>
                    <div className="fw-semibold" style={{ fontSize: "0.85rem" }}>{sessionWindow}</div>
                  </div>
                )}
              </div>

              <div className="d-flex justify-content-between gap-2 flex-wrap">
                <Button type="button" className="rounded-4" onClick={handleClose}>
                  Dismiss
                </Button>
                <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                  Scan again
                </Button>
              </div>
            </div>
          );
        })()}

        {/* ── Error stage ── */}
        {stage === "error" && (
          <>
            <Alert variant="warning" className="mb-3 rounded-3">
              {errorMessage ?? "An unknown error occurred."}
            </Alert>
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" className="rounded-4" onClick={handleClose}>
                Close
              </Button>
              <Button type="button" className="rounded-4 btn-main" onClick={handleRetry}>
                Try again
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}
